import React, { useMemo, DependencyList, ReactNode } from 'react';
import { ColumnDef } from './DataTable.types';

/**
 * Hook that extracts stable ColumnDef[] from declarative DataTableColumn JSX.
 * Only recalculates when dependencies change — prevents unnecessary re-renders.
 *
 * Usage:
 * const columns = useColumns<MyType>(() => (
 *     <>
 *         <DataTableColumn id="col-1" field="name" title="Nome" width="200px" canSort>
 *             {(row) => <span>{row.name}</span>}
 *         </DataTableColumn>
 *         <DataTableColumn id="col-2" field="city" title="Cidade" width="150px" canSort />
 *     </>
 * ), [dep1, dep2]);
 */
export function useColumns<T>(factory: () => ReactNode, deps: DependencyList): ColumnDef<T>[] {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => extractColumns<T>(factory()), deps);
}

function walk<T>(children: ReactNode, cols: ColumnDef<T>[]) {
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === React.Fragment) {
            walk(child.props.children, cols);
            return;
        }
        const props = child.props as any;
        if (!props.id || !props.field) return;
        cols.push({
            id: props.id,
            field: props.field,
            title: props.title,
            width: props.width,
            canSort: props.canSort,
            sticky: props.sticky,
            align: props.align,
            filterType: props.filterType,
            filterOptions: props.filterOptions,
            renderCell: typeof props.children === 'function' ? props.children : undefined,
            renderHeader: props.renderHeader,
            bgClassName: props.bgClassName,
            group: props.group,
            groupColor: props.groupColor,
        });
    });
}

function extractColumns<T>(nodes: ReactNode): ColumnDef<T>[] {
    const cols: ColumnDef<T>[] = [];
    walk(nodes, cols);
    return cols;
}
